import React, { FC, Fragment, useCallback, useState } from 'react';
import AddTechniciansDialog from 'components/users/technicians/add-technicians-dialog/add-technicians-dialog.component';
import TechniciansList from 'components/users/technicians/technicians-list/technicians-list.component';
import TechniciansPageActions from 'components/users/technicians/technicians-page-actions/technicians-page-actions.component';
import { NetworkStatus, useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import debounce from 'lodash.debounce';
import { Organization } from 'models/organization';
import PageTitle from 'components/shared/page-title/page-title.component';
import { User, UserListFilter } from 'models/user';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { useTranslation } from 'react-i18next';
import { currentUserVar } from 'apollo/reactive-variables/user';

const Technicians: FC = () => {
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [addSnackbarOpen, setAddSnackbarOpen] = useState(false);
    const [lastOffset, setLastOffset] = useState(0);
    const [technicians, setTechnicians] = useState<User[]>([]);
    const [filter, setFilter] = useState<UserListFilter>({
        freeText: '',
        active: true,
    });
    const { t: translate } = useTranslation(['users']);

    const {
        loading: techniciansLoading,
        networkStatus,
        fetchMore,
        refetch,
    } = useQuery(getTechniciansForUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: filter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setTechnicians(technicians);
            setLastOffset(technicians.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: UserListFilter) => {
            setFilter({ freeText: freeText, active: active });
            setLastOffset(0);
        }, 300),
        [],
    );

    const dialogOpenHandler = () => {
        setAddSnackbarOpen(false);
        setIsDialogOpen(true);
    };

    const dialogCloseHandler = () => {
        setIsDialogOpen(false);
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('common:USERS')} />
            <TechniciansPageActions onFilterChange={handleFilterChange} onDialogOpenClick={dialogOpenHandler} />
            {techniciansLoading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <Fragment>
                    <TechniciansList
                        technicians={technicians}
                        isTechniciansLoading={techniciansLoading}
                        onFetchMoreChange={handleFetchMore}
                        onUpdateTechnicianClick={refetch}
                    />
                    <AddTechniciansDialog
                        isDialogOpen={isDialogOpen}
                        onDialogCloseClick={dialogCloseHandler}
                        onAddTechnicianClick={() => {
                            setAddSnackbarOpen(true);
                            refetch();
                        }}
                        onUserLinked={() => {
                            refetch();
                        }}
                    />
                </Fragment>
            )}
            {addSnackbarOpen ? <AlertSnackbar type="success" message={translate('USER_ADDED_MESSAGE')} /> : undefined}
        </Fragment>
    );
};

export default Technicians;
